import React, { useEffect, useState } from 'react';
import { collection, query, orderBy, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import TicketDetails from './TicketDetails';

function TicketList({ tickets: propTickets, loading: propLoading }) {
  // Verwenden Sie entweder die übergebenen Props oder initialisieren Sie eigene Zustände
  const [tickets, setTickets] = useState(propTickets || []);
  const [loading, setLoading] = useState(propLoading !== undefined ? propLoading : true);
  const [selectedTicket, setSelectedTicket] = useState(null);

  // Nur laden, wenn keine Tickets als Props übergeben wurden
  useEffect(() => {
    if (propTickets) {
      return; // Überspringen, wenn Tickets als Props übergeben wurden
    }

    const q = query(collection(db, 'tickets'), orderBy('createdAt', 'desc'));
    
    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      const ticketList = [];
      querySnapshot.forEach((doc) => {
        ticketList.push({
          id: doc.id,
          ...doc.data()
        });
      });
      setTickets(ticketList);
      setLoading(false);
    }, (error) => {
      console.error("Fehler beim Laden der Tickets:", error);
      setLoading(false);
    });
    
    return () => unsubscribe();
  }, [propTickets]);

  // Wenn sich die propTickets ändern, aktualisieren wir den lokalen Zustand
  useEffect(() => {
    if (propTickets) {
      setTickets(propTickets);
      setLoading(propLoading !== undefined ? propLoading : false);
    }
  }, [propTickets, propLoading]);

  // Funktion zum Öffnen eines Tickets
  const openTicket = (ticket) => {
    setSelectedTicket(ticket);
  };

  // Funktion zum Schließen des Ticket-Details-Dialogs
  const closeTicket = () => {
    setSelectedTicket(null);
  };

  // Funktion zum Aktualisieren eines Tickets in der lokalen Liste
  const updateTicket = (updatedTicket) => {
    setTickets(tickets.map(ticket => 
      ticket.id === updatedTicket.id ? updatedTicket : ticket
    ));
    setSelectedTicket(updatedTicket);
  };

  // Funktion zum Löschen eines Tickets
  const handleDeleteTicket = (deletedTicketId) => {
    // Entferne das gelöschte Ticket aus der lokalen Liste
    setTickets(tickets.filter(ticket => ticket.id !== deletedTicketId));
  };

  // Übersetze Status für die Anzeige
  const getStatusDisplay = (status) => {
    switch(status) {
      case 'new': return 'Neu';
      case 'in-progress': return 'In Bearbeitung';
      case 'resolved': return 'Erledigt';
      default: return status;
    }
  };

  if (loading) {
    return <div>Tickets werden geladen...</div>;
  }

  if (tickets.length === 0) {
    return <div>Keine Tickets vorhanden.</div>;
  }

  return (
    <div>
      <h2>Feedback-Tickets</h2>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={tableHeaderStyle}>Titel</th>
              <th style={tableHeaderStyle}>Beschreibung</th>
              <th style={tableHeaderStyle}>Status</th>
              <th style={tableHeaderStyle}>Priorität</th>
              <th style={tableHeaderStyle}>Erstellt am</th>
              <th style={tableHeaderStyle}>Benutzer</th>
              <th style={tableHeaderStyle}>App-Version</th>
            </tr>
          </thead>
          <tbody>
            {tickets.map(ticket => (
              <tr 
                key={ticket.id} 
                onClick={() => openTicket(ticket)}
                style={{
                  cursor: 'pointer',
                  transition: 'background-color 0.2s',
                  backgroundColor: 
                    ticket.status === 'new' ? 'rgba(255, 205, 210, 0.1)' : 
                    ticket.status === 'in-progress' ? 'rgba(255, 249, 196, 0.1)' : 
                    'rgba(200, 230, 201, 0.1)',
                  ':hover': {
                    backgroundColor: '#f5f5f5'
                  }
                }}
              >
                <td style={tableCellStyle}>{ticket.title}</td>
                <td style={tableCellStyle}>
                  {ticket.description.length > 50 
                    ? `${ticket.description.substring(0, 50)}...` 
                    : ticket.description}
                </td>
                <td style={tableCellStyle}>
                  <span style={{
                    padding: '4px 8px',
                    borderRadius: '4px',
                    backgroundColor: 
                      ticket.status === 'new' ? '#ffcdd2' : 
                      ticket.status === 'in-progress' ? '#fff9c4' : 
                      '#c8e6c9',
                    fontSize: '12px'
                  }}>
                    {getStatusDisplay(ticket.status)}
                  </span>
                </td>
                <td style={tableCellStyle}>{ticket.priority}</td>
                <td style={tableCellStyle}>
                  {ticket.createdAt?.toDate ? 
                    ticket.createdAt.toDate().toLocaleDateString() : 
                    'N/A'}
                </td>
                <td style={tableCellStyle}>{ticket.userEmail || 'N/A'}</td>
                <td style={tableCellStyle}>{ticket.appVersion || 'N/A'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Ticket-Details-Dialog */}
      {selectedTicket && (
        <TicketDetails 
          ticket={selectedTicket} 
          onClose={closeTicket} 
          onUpdate={updateTicket}
          onDelete={handleDeleteTicket}
        />
      )}
    </div>
  );
}

const tableHeaderStyle = {
  textAlign: 'left',
  padding: '12px 8px',
  borderBottom: '2px solid #e0e0e0',
  backgroundColor: '#f5f5f5'
};

const tableCellStyle = {
  padding: '8px',
  borderBottom: '1px solid #e0e0e0'
};

export default TicketList;